import React, { useEffect, useMemo, useState } from "react";
import "../opportunities/OpportunityWorkspace.css";
import type { ActionBrief } from "../../lib/actionBriefs";
import { COMPETITIVE_COMPLETION_MEMORY_DAYS, filterCompetitiveOpportunitiesByCompletionMemory, getCompetitiveSemanticIdentity } from "../../lib/actionBriefs";
import { supabase } from "../../lib/supabase";
import Tooltip from "../Tooltip";
import OpportunityCard from "./OpportunityCard";
import ModuleContainer from "./ModuleContainer";
import OpportunityActionBriefPanel from "../opportunities/OpportunityActionBriefPanel";
import { buildContentDraftOutputModel } from "../../lib/currentAssetIntelligence/contentDraftOutputModel";
import ContentBriefAction from "../opportunities/ContentBriefAction";
import { OpportunityWorkspaceHeader } from "../opportunities/OpportunityPresentation";

type Opportunity = {
  competitor: string;
  gap_count: number;
  prompt_focus: string;
  prompt_focus_label?: string;
  recommended_action?: string;
  impact_level: "High" | "Medium" | "Low";
  run_id?: string;
  run_date?: string | null;
};

type EntityBuilderRecommendation = {
  key: string;
  title: string;
  description: string;
  action: string;
  activationLabel: string;
  activationType?: string | null;
  priority?: string;
  entityName?: string | null;
  entityArea?: string | null;
  recommendationType?: string | null;
  source: "backend" | "derived";
  actionBriefId?: string | null;
  actionBriefStatus?: string | null;
};

type BackendEntityBuilderRow = {
  id?: string;
  recommendation_type?: string | null;
  entity_area?: string | null;
  entity_name?: string | null;
  title?: string | null;
  summary?: string | null;
  why_it_matters?: string | null;
  recommended_action?: string | null;
  activation_type?: string | null;
  priority?: string | null;
  confidence_score?: number | null;
};

type GovernedMentionRow = {
  display_name?: string | null;
  raw_entity_name?: string | null;
  category?: string | null;
  prompt_type?: string | null;
  run_id?: string | null;
};

type Props = {
  account: string;
  mode?: "demo" | "competitive";
  accountType?: string | null;
  entitlementOverride?: unknown;
  customerBrand?: string;
  customerRank?: number;
  runId?: string | null;
  presentation?: "full" | "focused";
  initialVisibleCount?: number;
};

type ModuleStatus = "loading" | "ready" | "empty" | "error" | "unavailable";

type ProductAreaCompletion = {
  key: string;
  label: string;
  count: number;
};

type CompletionSummary = {
  currentRun: number;
  last30: number;
  ytd: number;
  allTime: number;
  currentYear: number;
  byProductArea: ProductAreaCompletion[];
};

type CompletedOpportunityWork = {
  id: string;
  title?: string | null;
  action_type?: string | null;
  product_area?: string | null;
  marked_complete_at?: string | null;
  completed_by?: string | null;
};

type CompletedCompetitiveBrief = Partial<ActionBrief> & { id: string };

const EMPTY_COMPLETION_SUMMARY: CompletionSummary = {
  currentRun: 0,
  last30: 0,
  ytd: 0,
  allTime: 0,
  currentYear: new Date().getFullYear(),
  byProductArea: [
    { key: "competitive", label: "Competitive", count: 0 },
    { key: "ai_presence_builder", label: "AI Presence Builder", count: 0 },
  ],
};

const OPPORTUNITY_DISPLAY_LIMIT = 100;
const OPPORTUNITY_QUICK_JUMP_LIMIT = 8;
const ENTITY_RECOMMENDATION_LIMIT = 6;

function FocusedWorkSummary({
  title,
  action,
  outputType,
  targetAsset,
  audience,
  evidencePattern,
  expectedResult,
  draftStatus,
  accountId,
  runId,
  recommendationId,
  recommendationKey,
  recommendationType,
  targetEntity,
  recommendedOutput,
  confidence,
  liveCaiEnabled,
  actionBriefId,
  accountType,
  entitlementOverride,
  accountName,
}: {
  title: string;
  action: string;
  outputType: string;
  targetAsset: string;
  audience: string;
  evidencePattern: string;
  expectedResult: string;
  draftStatus: string;
  accountId: string;
  runId?: string | null;
  recommendationId?: string | null;
  recommendationKey?: string | null;
  recommendationType?: string | null;
  targetEntity?: string | null;
  recommendedOutput?: string | null;
  confidence?: string | number | null;
  liveCaiEnabled?: boolean;
  actionBriefId?: string | null;
  accountType?: string | null;
  entitlementOverride?: unknown;
  accountName?: string | null;
}) {
  return <section className="oe-selected-work toren-surface" data-testid="oe-next-focused-work" style={{ marginTop: 10, order: 1 }}>
    <div style={{ color: "#2563eb", fontSize: 11, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>Next focused work</div>
    <div className="oe-selected-work-facts" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, marginTop: 8 }}>
      <div><div style={{ color: "#64748b", fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>Recommended focus</div><div style={{ marginTop: 3, color: "#172554", fontWeight: 900 }}>{title}</div></div>
      <div data-testid="oe-recommended-output"><div style={{ color: "#64748b", fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>Recommended output</div><div style={{ marginTop: 3, color: "#172554", fontSize: 13, fontWeight: 900 }}>{outputType}</div><div style={{ marginTop: 2, color: "#64748b", fontSize: 12 }}>Target asset: {targetAsset}</div><div style={{ marginTop: 2, color: "#64748b", fontSize: 12 }}>For: {audience}</div></div>
      <div data-testid="oe-why-this-next"><div style={{ color: "#64748b", fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>Why it matters</div><div style={{ marginTop: 4, color: "#475569", fontSize: 12, lineHeight: 1.4 }}>{evidencePattern}</div></div>
      {expectedResult && expectedResult !== evidencePattern && <div data-testid="oe-expected-result"><div style={{ color: "#64748b", fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>Expected result</div><div style={{ marginTop: 3, color: "#475569", fontSize: 12, lineHeight: 1.4 }}>{expectedResult}</div></div>}
      <div><div style={{ color: "#64748b", fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>Recommended next step</div><div style={{ marginTop: 3, color: "#1d4ed8", fontSize: 13, fontWeight: 800, lineHeight: 1.4 }}>{action}</div></div>
    </div>
    <FutureOutputIndicators draftStatus={draftStatus} />
    <details style={{ marginTop: 10 }}><summary>Brief types and availability</summary><p style={{ color: "#475569", fontSize: 12 }}>Build Action Plan opens the existing Action Brief workflow below. Comparison recommendations retain their Comparison Brief pathway. CAI optimization briefs for reviewed pages are available in Assets.</p><ContentBriefAction
      accountId={accountId}
      runId={runId}
      recommendationId={recommendationId}
      recommendationKey={recommendationKey || recommendationId}
      recommendationType={recommendationType}
      targetEntity={targetEntity}
      recommendedOutput={recommendedOutput}
      confidence={confidence}
      opportunityCategory="entity"
      liveCaiEnabled={liveCaiEnabled}
      actionBriefId={actionBriefId}
      actionBriefReady={draftStatus === "Ready for draft" || draftStatus === "Draft prepared"}
      accountType={accountType}
      entitlementOverride={entitlementOverride}
      productArea="competitive"
      accountName={accountName}
      recommendationTitle={title}
      targetAsset={targetAsset}
      targetAudience={audience}
      evidencePattern={evidencePattern}
      objective={expectedResult}
      expectedResult={expectedResult}
    /></details>
  </section>;
}

function getCompetitivePrescribedOutput(
  recommendation: EntityBuilderRecommendation,
  accountId: string,
  runId?: string | null,
) {
  return buildContentDraftOutputModel({
    recommendation,
    recommendationId: recommendation.key,
    accountId,
    runId,
    actionBriefId: recommendation.actionBriefId,
    actionBriefStatus: recommendation.actionBriefStatus,
  });
}

function FutureOutputIndicators({ draftStatus }: { draftStatus: string }) {
  const draftHelper = draftStatus === "Ready for draft" || draftStatus === "Draft prepared"
    ? "Ready for draft · generation not enabled yet"
    : "Requires reviewed action brief";
  return <details data-testid="oe-future-output-indicators" style={{ marginTop: 10 }}><summary>Future draft delivery — not available</summary><div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
    <button type="button" disabled aria-disabled="true" title={draftHelper} style={{ border: "1px solid #cbd5e1", borderRadius: 999, padding: "5px 8px", background: "#f8fafc", color: "#64748b", fontSize: 11, fontWeight: 800, cursor: "not-allowed" }}>Generate Content Draft</button>
    <span style={{ color: "#64748b", fontSize: 11, alignSelf: "center" }}>{draftHelper}</span>
    <button type="button" disabled aria-disabled="true" title="Draft delivery not connected yet." style={{ border: "1px solid #cbd5e1", borderRadius: 999, padding: "5px 8px", background: "#f8fafc", color: "#64748b", fontSize: 11, fontWeight: 800, cursor: "not-allowed" }}>Prepare WordPress Draft · Coming soon</button>
    <button type="button" disabled aria-disabled="true" title="Draft delivery not connected yet." style={{ border: "1px solid #cbd5e1", borderRadius: 999, padding: "5px 8px", background: "#f8fafc", color: "#64748b", fontSize: 11, fontWeight: 800, cursor: "not-allowed" }}>Prepare Shopify Draft · Coming soon</button>
  </div></details>;
}

function FocusedSecondaryCompetitiveRow({
  recommendation,
  selected,
  onSelect,
  account,
  runId,
  displayCustomerBrand,
  onCompletionChange,
}: {
  recommendation: EntityBuilderRecommendation;
  selected: boolean;
  onSelect: () => void;
  account: string;
  runId?: string | null;
  displayCustomerBrand: string;
  onCompletionChange: (brief: CompletedCompetitiveBrief) => void;
}) {
  const output = getCompetitivePrescribedOutput(recommendation, account, runId);
  return <div data-testid="oe-secondary-opportunity" style={{ border: "1px solid #dbeafe", borderRadius: 12, padding: "10px 12px", background: "#ffffff" }}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
      <div>
        <div style={{ color: "#0f172a", fontSize: 13, fontWeight: 850 }}>{recommendation.title}</div>
        <div style={{ marginTop: 3, color: "#64748b", fontSize: 11 }}>{recommendation.priority || "Priority unavailable"} · {output.outputType}</div>
      </div>
      <button type="button" onClick={onSelect} aria-expanded={selected} style={{ border: "1px solid #93c5fd", borderRadius: 999, padding: "5px 9px", background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>{selected ? "Hide" : "Select"}</button>
    </div>
    {selected && <div style={{ marginTop: 9, borderTop: "1px solid #dbeafe", paddingTop: 9 }}>
      <div style={{ color: "#475569", fontSize: 12, lineHeight: 1.45 }}>{recommendation.description}</div>
      <div style={{ marginTop: 6, color: "#0369a1", fontSize: 12, fontWeight: 800 }}>{recommendation.action}</div>
      <OpportunityActionBriefPanel
        accountId={account}
        runId={runId ?? null}
        productArea="competitive"
        opportunity={{
          title: recommendation.title,
          summary: recommendation.description,
          recommendation: recommendation.action,
          category: recommendation.entityArea || recommendation.recommendationType || "entity_builder",
          intent: recommendation.activationType || recommendation.activationLabel || "generate_action_brief",
          priority: recommendation.priority,
          evidence: [`Recommended activation: ${recommendation.activationLabel}`],
          competitors: recommendation.entityName ? [recommendation.entityName] : undefined,
        }}
        accountContext={{ accountName: displayCustomerBrand, category: recommendation.entityArea || recommendation.recommendationType || "AI presence builder" }}
        onCompletionChange={onCompletionChange}
      />
    </div>}
  </div>;
}

const CATEGORY_LABELS: Record<string, string> = {
  category_discovery: "Category Discovery",
  alternatives_replacements: "Alternatives",
  comparisons: "Comparisons",
  use_case_fit: "Use-Case Fit",
  pain_point_led: "Pain Points",
  purchase_shortlist: "Purchase Shortlist",

  Discovery: "Discovery",
  Recommendation: "Recommendation",
  Comparison: "Comparison",
  Consideration: "Consideration",
  Opportunity: "Opportunity Area",
};

const ACTIVATION_LABELS: Record<string, string> = {
  generate_entity_brief: "Generate Entity Brief",
  review_entity_mapping: "Review Entity Mapping",
  generate_alias_review: "Generate Alias Review",
  generate_suppression_review: "Generate Suppression Review",
  generate_proof_checklist: "Generate Proof Checklist",
  generate_schema_checklist: "Generate Schema Checklist",
  generate_comparison_brief: "Generate Comparison Brief",
  generate_content_brief: "Generate Content Brief",
  generate_action_brief: "Generate Action Brief",
};

const FOUNDATION_PILLARS = [
  {
    title: "Entity clarity",
    description:
      "Make sure AI systems can identify who the brand is, what it does, who it serves, and how its names, acronyms, products, and programs connect.",
  },
  {
    title: "Buyer-journey coverage",
    description:
      "Build durable answers for the questions buyers ask before they know the brand, while comparing options, and when choosing a shortlist.",
  },
  {
    title: "Competitive proof",
    description:
      "Explain why the brand should be considered alongside or instead of the competitors currently winning AI visibility.",
  },
  {
    title: "Trust and authority",
    description:
      "Strengthen third-party proof, expertise, reviews, citations, and credibility signals that help AI systems recommend the brand with confidence.",
  },
];

const FOUNDATION_METRIC_TOOLTIPS: Record<string, string> = {
  "Gap signals":
    "A gap signal is a prompt-level moment where another entity is included in the AI response and your brand is not. More signals means the pattern is more consistent, not just a one-off miss.",
  Competitors:
    "Unique competitor or market entities currently creating AI visibility pressure across the selected focus area.",
  "Focus areas":
    "The number of buyer-journey groups where Toren found opportunities, such as discovery, comparisons, use-case fit, or purchase shortlist.",
  "High impact":
    "High-impact opportunities have five or more gap signals. These are repeated patterns that should usually be addressed before lower-volume items.",
};

function normalizeCategory(category?: string) {
  const value = (category || "").trim().toLowerCase();

  switch (value) {
    case "category_discovery":
    case "discovery":
    case "brand interpretation":
    case "publishing use case":
      return "category_discovery";

    case "alternatives_replacements":
      return "alternatives_replacements";

    case "comparisons":
    case "comparison":
      return "comparisons";

    case "use_case_fit":
    case "consideration":
    case "core category":
      return "use_case_fit";

    case "pain_point_led":
      return "pain_point_led";

    case "purchase_shortlist":
    case "recommendation":
    case "strategic":
      return "purchase_shortlist";

    default:
      return "Opportunity";
  }
}

function maskText(text: string, demoMode?: boolean) {
  if (!demoMode) return text;
  return "Top Competitor 🔒";
}

function normalizeEntity(value?: string) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function formatBrandDisplay(value?: string) {
  const raw = String(value || "").trim();
  if (!raw) return "Your brand";

  const normalized = raw.toLowerCase();
  const acronymMap: Record<string, string> = {
    bwjp: "BWJP",
  };

  return acronymMap[normalized] || raw;
}

function getFocusLabel(focus?: string, fallback?: string) {
  return fallback || CATEGORY_LABELS[normalizeCategory(focus)] || focus || "Opportunity";
}

function getActivationLabel(value?: string | null) {
  const key = String(value || "generate_action_brief").trim();
  return ACTIVATION_LABELS[key] || "Generate Action Brief";
}

const ACTION_TYPE_COLOR_KEY = [
  {
    label: "Comparison",
    note: "comparison work",
    border: "#bae6fd",
    background: "#f0f9ff",
    color: "#0369a1",
  },
  {
    label: "Entity",
    note: "entity clarity",
    border: "#ddd6fe",
    background: "#f5f3ff",
    color: "#5b21b6",
  },
  {
    label: "Review",
    note: "needs review",
    border: "#fed7aa",
    background: "#fff7ed",
    color: "#9a3412",
  },
  {
    label: "Content",
    note: "content/proof",
    border: "#bbf7d0",
    background: "#f0fdf4",
    color: "#166534",
  },
  {
    label: "Cleanup",
    note: "cleanup/suppression",
    border: "#fecaca",
    background: "#fff1f2",
    color: "#991b1b",
  },
];

function ActionTypeColorKey({ activeFilter, onFilter, counts = {} }: { activeFilter: string; onFilter: (value: string) => void; counts?: Record<string, number> }) {
  return (
    <div
      style={{
        margin: "0 0 14px",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        background: "#ffffff",
        padding: "10px 12px",
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <span
        style={{
          color: "#64748b",
          fontSize: 12,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginRight: 2,
        }}
      >
        Action/work type filters
      </span>

      {ACTION_TYPE_COLOR_KEY.map((item) => (
        <button
          key={item.label}
          type="button"
          title={`${item.label}: ${item.note}. Click to filter the visible Competitive action briefs.`}
          aria-pressed={activeFilter === item.label}
          disabled={(counts[item.label] || 0) === 0 && activeFilter !== item.label}
          onClick={() => onFilter(activeFilter === item.label ? "all" : item.label)}
          className="toren-control toren-control--secondary"
        >
          {item.label}: {item.note} ({counts[item.label] || 0})
        </button>
      ))}
      {activeFilter !== "all" && (
        <button type="button" onClick={() => onFilter("all")} className="toren-control toren-control--tertiary">
          Clear filter
        </button>
      )}
    </div>
  );
}

function getCompetitiveActionClassification(recommendation: EntityBuilderRecommendation) {
  const combined = [
    recommendation.activationType,
    recommendation.activationLabel,
    recommendation.entityArea,
    recommendation.recommendationType,
    recommendation.title,
    recommendation.description,
    recommendation.action,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    combined.includes("suppress") ||
    combined.includes("false positive") ||
    combined.includes("cleanup") ||
    combined.includes("hide")
  ) {
    return { key: "Cleanup", style: { border: "1px solid #fecaca", background: "#fff1f2" } };
  }

  if (
    combined.includes("review_entity_mapping") ||
    combined.includes("review mapping") ||
    combined.includes("governance") ||
    combined.includes("alias") ||
    combined.includes("review")
  ) {
    return { key: "Review", style: { border: "1px solid #fed7aa", background: "#fff7ed" } };
  }

  if (
    combined.includes("entity") ||
    combined.includes("schema") ||
    combined.includes("clarity")
  ) {
    return { key: "Entity", style: { border: "1px solid #ddd6fe", background: "#f5f3ff" } };
  }

  if (
    combined.includes("content") ||
    combined.includes("cluster") ||
    combined.includes("page") ||
    combined.includes("faq") ||
    combined.includes("proof") ||
    combined.includes("checklist")
  ) {
    return { key: "Content", style: { border: "1px solid #bbf7d0", background: "#f0fdf4" } };
  }

  if (
    combined.includes("comparison") ||
    combined.includes("competitor") ||
    combined.includes("competitive")
  ) {
    return { key: "Comparison", style: { border: "1px solid #bae6fd", background: "#f0f9ff" } };
  }

  return { key: null, style: { border: "1px solid #e2e8f0", background: "#ffffff" } };
}

function getEntityBuilderRecommendationStyle(
  recommendation: EntityBuilderRecommendation
): React.CSSProperties {
  return getCompetitiveActionClassification(recommendation).style;
}

function getFocusedPriorityRank(priority?: string | null) {
  const normalized = String(priority || "").toLowerCase();
  if (normalized === "high") return 0;
  if (normalized === "medium") return 1;
  if (normalized === "low") return 2;
  return 3;
}

function getFocusedSignalScore(value: string) {
  const text = value.toLowerCase();
  return (/(repeat|repeated|gap|missing|absent)/.test(text) ? 2 : 0)
    + (/(evidence|proof|output|brief|recommend)/.test(text) ? 1 : 0);
}

function sortFocusedCompetitiveRecommendations(items: EntityBuilderRecommendation[]) {
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const priorityDifference = getFocusedPriorityRank(left.item.priority) - getFocusedPriorityRank(right.item.priority);
      if (priorityDifference !== 0) return priorityDifference;
      const leftSignalScore = getFocusedSignalScore([left.item.title, left.item.description, left.item.action, left.item.recommendationType].filter(Boolean).join(" "));
      const rightSignalScore = getFocusedSignalScore([right.item.title, right.item.description, right.item.action, right.item.recommendationType].filter(Boolean).join(" "));
      if (leftSignalScore !== rightSignalScore) return rightSignalScore - leftSignalScore;
      return left.index - right.index;
    })
    .map(({ item }) => item);
}

function getCompletionWindows() {
  const now = new Date();
  const last30 = new Date(now);
  last30.setDate(now.getDate() - 30);

  const ytdStart = new Date(now.getFullYear(), 0, 1);

  return {
    last30Start: last30.toISOString(),
    ytdStart: ytdStart.toISOString(),
    currentYear: now.getFullYear(),
  };
}

function formatCompletionDate(value?: string | null) {
  if (!value) return "Completed";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Completed";

  return `Completed ${date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
}

function formatProductArea(value?: string | null) {
  const area = String(value || "").trim();
  const match = EMPTY_COMPLETION_SUMMARY.byProductArea.find((item) => item.key === area);
  return match?.label || "Opportunity";
}

function formatActionType(value?: string | null) {
  return String(value || "Action")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function mapBackendRecommendation(row: BackendEntityBuilderRow): EntityBuilderRecommendation {
  const title = row.title || row.recommendation_type || "Entity Builder Recommendation";

  return {
    key: row.id || `${title}-${row.entity_name || row.entity_area || Math.random()}`,
    title,
    description:
      row.summary ||
      row.why_it_matters ||
      "Agent Smith identified an entity-building opportunity that should be reviewed before activation.",
    action:
      row.recommended_action ||
      "Review this recommendation and generate the right execution brief for the account.",
    activationLabel: getActivationLabel(row.activation_type),
    activationType: row.activation_type || "generate_action_brief",
    priority: row.priority || undefined,
    entityName: row.entity_name || null,
    entityArea: row.entity_area || null,
    recommendationType: row.recommendation_type || null,
    source: "backend",
  };
}

export default function OpportunityEngine({
  account,
  mode = "competitive",
  accountType,
  entitlementOverride,
  customerBrand,
  customerRank,
  runId,
  presentation = "full",
  initialVisibleCount = 5,
}: Props) {
  const [data, setData] = useState<Opportunity[]>([]);
  const [backendEntityRecommendations, setBackendEntityRecommendations] = useState<EntityBuilderRecommendation[]>([]);
  const [status, setStatus] = useState<ModuleStatus>("loading");
  const [statusMessage, setStatusMessage] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedPromptFocus, setSelectedPromptFocus] = useState("all");
  const [completionSummary, setCompletionSummary] = useState<CompletionSummary>(EMPTY_COMPLETION_SUMMARY);
  const [completionStatus, setCompletionStatus] = useState<"loading" | "ready" | "error">("loading");
  const [recentCompletedWork, setRecentCompletedWork] = useState<CompletedOpportunityWork[]>([]);
  const [completedCompetitiveBriefs, setCompletedCompetitiveBriefs] = useState<CompletedCompetitiveBrief[]>([]);
  const [competitiveCompletionMemory, setCompetitiveCompletionMemory] = useState<CompletedCompetitiveBrief[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionTypeFilter, setActionTypeFilter] = useState("all");
  const [showAllRecommendations, setShowAllRecommendations] = useState(false);
  const [selectedFocusedRecommendationKey, setSelectedFocusedRecommendationKey] = useState<string | null>(null);
  const isFocused = presentation === "focused";
  const focusedVisibleCount = Math.max(1, Math.min(initialVisibleCount, 5));
  const isLeaderAccount = customerRank === 1;
  const demoMode = mode === "demo";
  const displayCustomerBrand = formatBrandDisplay(customerBrand);

  useEffect(() => {
    if (!account) return;

    const fetchData = async () => {
      setStatus("loading");
      setStatusMessage("");
      setBackendEntityRecommendations([]);

      const latestRunId = runId;

      if (!latestRunId) {
        setData([]);
        setActiveIndex(0);
        setStatus("empty");
        setStatusMessage("Not enough data available yet.");
        return;
      }

      const extractBrandMentions = (value: any): string[] => {
        if (Array.isArray(value)) {
          return value
            .filter((item) => typeof item === "string")
            .map((item) => item.trim())
            .filter(Boolean);
        }

        if (typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
              return parsed
                .filter((item) => typeof item === "string")
                .map((item) => item.trim())
                .filter(Boolean);
            }
          } catch {
            return value
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean);
          }
        }

        return [];
      };

      const [governedRunResult, rawOpportunityResult, recommendationResult] = await Promise.all([
        supabase.rpc("toren_entity_mentions_governed_for_run", {
          p_account_id: account,
          p_run_id: latestRunId,
        }),
        supabase
          .from("prompt_results")
          .select("display_brand_mentions, brand_mentions, category, prompt_type, visibility_signal")
          .eq("account_id", account)
          .eq("run_id", latestRunId)
          .neq("visibility_signal", "error")
          .limit(1000),
        supabase
          .from("entity_builder_recommendations_open_v1")
          .select(
            "id,recommendation_type,entity_area,entity_name,title,summary,why_it_matters,recommended_action,activation_type,priority,confidence_score"
          )
          .eq("account_id", account)
          .or(`run_id.eq.${latestRunId},run_id.is.null`)
          .limit(ENTITY_RECOMMENDATION_LIMIT),
      ]);

      if (recommendationResult.error) {
        console.warn(
          "[OpportunityEngine] Entity Builder recommendation query error",
          recommendationResult.error
        );
      } else {
        setBackendEntityRecommendations(
          ((recommendationResult.data || []) as BackendEntityBuilderRow[]).map(mapBackendRecommendation)
        );
      }

      if (governedRunResult.error && rawOpportunityResult.error) {
        console.warn("[OpportunityEngine] Opportunity query error", {
          governed: governedRunResult.error,
          raw: rawOpportunityResult.error,
        });
        setData([]);
        setActiveIndex(0);
        setStatus("empty");
        setStatusMessage("Opportunity data is being prepared.");
        return;
      }

      if (governedRunResult.error) {
        console.warn(
          "[OpportunityEngine] Run-scoped governed mention RPC unavailable; falling back to prompt_results",
          governedRunResult.error
        );
      }

      const customerKey = normalizeEntity(customerBrand);
      const opportunityMap = new Map<string, Opportunity>();

      function addOpportunity({
        brand,
        focus,
        focusLabel,
      }: {
        brand?: string | null;
        focus?: string | null;
        focusLabel?: string | null;
      }) {
        const cleanBrand = String(brand || "").trim();
        if (!cleanBrand) return;

        const brandKey = normalizeEntity(cleanBrand);
        if (!brandKey || brandKey === customerKey) return;

        const normalizedFocus = focus || "category_discovery";
        const label =
          focusLabel ||
          CATEGORY_LABELS[normalizeCategory(normalizedFocus)] ||
          CATEGORY_LABELS[normalizedFocus] ||
          normalizedFocus;

        const key = `${brandKey}|||${normalizedFocus}`;
        const existing = opportunityMap.get(key);

        if (existing) {
          existing.gap_count += 1;
        } else {
          opportunityMap.set(key, {
            competitor: cleanBrand,
            gap_count: 1,
            prompt_focus: normalizedFocus,
            prompt_focus_label: label,
            impact_level: "Medium",
            run_id: latestRunId,
            run_date: null,
          });
        }
      }

      const governedRows = !governedRunResult.error
        ? ((governedRunResult.data || []) as GovernedMentionRow[])
        : [];

      if (governedRows.length > 0) {
        governedRows.forEach((row) => {
          const focus = row.category || row.prompt_type || "category_discovery";
          addOpportunity({
            brand: row.display_name || row.raw_entity_name,
            focus,
          });
        });
      } else {
        (rawOpportunityResult.data || []).forEach((row: any) => {
          const focus = row.category || row.prompt_type || "category_discovery";
          const focusLabel =
            CATEGORY_LABELS[normalizeCategory(focus)] ||
            CATEGORY_LABELS[focus] ||
            focus;

          const mentionSource =
            Array.isArray(row.display_brand_mentions) && row.display_brand_mentions.length
              ? row.display_brand_mentions
              : row.brand_mentions;

          extractBrandMentions(mentionSource).forEach((brand) => {
            addOpportunity({
              brand,
              focus,
              focusLabel,
            });
          });
        });
      }

      const allOpportunityRows = Array.from(opportunityMap.values())
        .sort((a, b) => b.gap_count - a.gap_count)
        .map((row) => ({
          ...row,
          impact_level:
            row.gap_count >= 5 ? "High" : row.gap_count >= 2 ? "Medium" : "Low",
        })) as Opportunity[];


      const rows = allOpportunityRows.slice(0, OPPORTUNITY_DISPLAY_LIMIT);

      if (!rows.length) {
        setData([]);
        setActiveIndex(0);
        setStatus("empty");
        setStatusMessage("Not enough opportunity data available yet.");
        return;
      }

      setData(rows);
      setActiveIndex(0);
      setSelectedPromptFocus("all");
      setStatus("ready");
    };

    fetchData();
  }, [account, runId, customerBrand]);

  useEffect(() => {
    let isMounted = true;

    async function countCompletedActionBriefs({
      gte = null,
      activeRunId = null,
      productArea = null,
    }: {
      gte?: string | null;
      activeRunId?: string | null;
      productArea?: string | null;
    }) {
      let query = supabase
        .from("opportunity_action_briefs")
        .select("id", { count: "exact", head: true })
        .eq("account_id", account)
        .not("marked_complete_at", "is", null);

      if (gte) query = query.gte("marked_complete_at", gte);
      if (activeRunId) query = query.eq("run_id", activeRunId);
      if (productArea) query = query.eq("product_area", productArea);

      const { count, error } = await query;
      if (error) throw error;

      return count || 0;
    }

    async function loadRecentCompletedWork() {
      const { data, error } = await supabase
        .from("opportunity_action_briefs")
        .select("id,title,action_type,product_area,marked_complete_at,completed_by")
        .eq("account_id", account)
        .in(
          "product_area",
          EMPTY_COMPLETION_SUMMARY.byProductArea.map((area) => area.key)
        )
        .not("marked_complete_at", "is", null)
        .order("marked_complete_at", { ascending: false })
        .limit(4);

      if (error) throw error;

      return (data || []) as CompletedOpportunityWork[];
    }

    async function loadCompletedCompetitiveBriefs() {
      if (!runId) return [] as CompletedCompetitiveBrief[];

      const { data, error } = await supabase
        .from("opportunity_action_briefs")
        .select("*")
        .eq("account_id", account)
        .eq("run_id", runId)
        .eq("product_area", "competitive")
        .not("marked_complete_at", "is", null)
        .eq("is_active", true)
        .order("marked_complete_at", { ascending: false });

      if (error) throw error;
      return (data || []) as CompletedCompetitiveBrief[];
    }

    async function loadCompetitiveCompletionMemory() {
      if (!account) return [] as CompletedCompetitiveBrief[];
      const memoryStart = new Date(Date.now() - COMPETITIVE_COMPLETION_MEMORY_DAYS * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("opportunity_action_briefs")
        .select("*")
        .eq("account_id", account)
        .eq("product_area", "competitive")
        .not("marked_complete_at", "is", null)
        .eq("is_active", true)
        .gte("marked_complete_at", memoryStart)
        .order("marked_complete_at", { ascending: false });
      if (error) throw error;
      return (data || []) as CompletedCompetitiveBrief[];
    }

    async function loadCompletionSummary() {
      if (!account) {
        setCompletionSummary(EMPTY_COMPLETION_SUMMARY);
        setRecentCompletedWork([]);
        setCompletedCompetitiveBriefs([]);
        setCompetitiveCompletionMemory([]);
        setCompletionStatus("ready");
        return;
      }

      setCompletionStatus((current) => (current === "ready" ? "ready" : "loading"));

      try {
        const windows = getCompletionWindows();
        const productAreas = EMPTY_COMPLETION_SUMMARY.byProductArea;

        const [currentRun, last30, ytd, allTime, recentWork, completedCompetitive, completionMemory, ...productAreaCounts] = await Promise.all([
          runId
            ? countCompletedActionBriefs({ activeRunId: runId })
            : Promise.resolve(0),
          countCompletedActionBriefs({ gte: windows.last30Start }),
          countCompletedActionBriefs({ gte: windows.ytdStart }),
          countCompletedActionBriefs({}),
          loadRecentCompletedWork(),
          loadCompletedCompetitiveBriefs(),
          loadCompetitiveCompletionMemory(),
          ...productAreas.map((area) =>
            countCompletedActionBriefs({ productArea: area.key })
          ),
        ]);

        if (!isMounted) return;

        setCompletionSummary({
          currentRun,
          last30,
          ytd,
          allTime,
          currentYear: windows.currentYear,
          byProductArea: productAreas.map((area, index) => ({
            ...area,
            count: productAreaCounts[index] || 0,
          })),
        });
        setRecentCompletedWork(recentWork as CompletedOpportunityWork[]);
        setCompletedCompetitiveBriefs(completedCompetitive as CompletedCompetitiveBrief[]);
        setCompetitiveCompletionMemory(completionMemory as CompletedCompetitiveBrief[]);
        setCompletionStatus("ready");
      } catch (error) {
        console.warn("[OpportunityEngine] Completed opportunity summary failed", error);
        if (isMounted) {
          setCompletionSummary(EMPTY_COMPLETION_SUMMARY);
          setRecentCompletedWork([]);
          setCompletedCompetitiveBriefs([]);
          setCompetitiveCompletionMemory([]);
          setCompletionStatus("error");
        }
      }
    }

    loadCompletionSummary();
    const refreshTimer = window.setInterval(loadCompletionSummary, 10000);

    return () => {
      isMounted = false;
      window.clearInterval(refreshTimer);
    };
  }, [account, runId]);

  const completedCompetitiveSemanticKeys = useMemo(
    () => new Set(competitiveCompletionMemory.map((brief) => {
      const sourceContext = (brief.source_context || {}) as Record<string, unknown>;
      return getCompetitiveSemanticIdentity({
        productArea: brief.product_area,
        actionType: brief.action_type,
        title: brief.title,
        sourceContext,
        opportunity: sourceContext.opportunity as any,
        accountContext: sourceContext.accountContext as any,
      }).semanticKey;
    }).filter(Boolean)),
    [competitiveCompletionMemory]
  );

  const actionableData = useMemo(
    () => filterCompetitiveOpportunitiesByCompletionMemory(data, completedCompetitiveSemanticKeys),
    [data, completedCompetitiveSemanticKeys]
  );

  const promptFocusOptions = useMemo(() => {
    const labels = new Map<string, string>();
    const counts = new Map<string, number>();

    actionableData.forEach((item) => {
      if (!item.prompt_focus) return;

      labels.set(
        item.prompt_focus,
        item.prompt_focus_label ||
          CATEGORY_LABELS[normalizeCategory(item.prompt_focus)] ||
          item.prompt_focus
      );

      counts.set(item.prompt_focus, (counts.get(item.prompt_focus) || 0) + 1);
    });

    return [
      { value: "all", label: `All Focus Areas (${actionableData.length})` },
      ...Array.from(labels.entries()).map(([value, label]) => ({
        value,
        label: `${label} (${counts.get(value) || 0})`,
      })),
    ];
  }, [actionableData]);

  const filteredData = useMemo(() => {
    if (selectedPromptFocus === "all") return actionableData;
    return actionableData.filter((item) => item.prompt_focus === selectedPromptFocus);
  }, [actionableData, selectedPromptFocus]);

  useEffect(() => {
    if (activeIndex >= filteredData.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, filteredData.length]);

  const activeItem = useMemo(
    () => filteredData[activeIndex] || null,
    [filteredData, activeIndex]
  );

  const peekItems = useMemo(
    () => filteredData.slice(0, OPPORTUNITY_QUICK_JUMP_LIMIT),
    [filteredData]
  );

  const foundationMetrics = useMemo(() => {
    const totalGapSignals = filteredData.reduce(
      (sum, item) => sum + item.gap_count,
      0
    );
    const uniqueCompetitors = new Set(
      filteredData.map((item) => normalizeEntity(item.competitor)).filter(Boolean)
    ).size;
    const focusAreaCount = new Set(
      filteredData.map((item) => normalizeCategory(item.prompt_focus))
    ).size;
    const highImpactCount = filteredData.filter(
      (item) => item.impact_level === "High"
    ).length;

    return {
      totalGapSignals,
      uniqueCompetitors,
      focusAreaCount,
      highImpactCount,
    };
  }, [filteredData]);

  const summary = useMemo(() => {
    if (!filteredData.length) return null;

    const top = filteredData[0];
    const topCategory = normalizeCategory(top.prompt_focus);
    const highImpactCount = filteredData.filter(
      (item) => item.impact_level === "High"
    ).length;

    return {
      topCompetitor: top.competitor,
      topCategory,
      total: filteredData.length,
      highImpactCount,
      narrative: isLeaderAccount
        ? `${maskText(
            top.competitor,
            demoMode
          )} is the closest challenger in ${
            CATEGORY_LABELS[topCategory] || CATEGORY_LABELS.Opportunity
          }. Protect this lead before competitors narrow the gap.`
        : highImpactCount > 0
          ? `${maskText(
              top.competitor,
              demoMode
            )} is creating the clearest pressure, with the strongest repeated gap pattern in ${
              CATEGORY_LABELS[topCategory] || CATEGORY_LABELS.Opportunity
            }.`
          : `${
              CATEGORY_LABELS[topCategory] || CATEGORY_LABELS.Opportunity
            } is the clearest opening right now, led by pressure from ${maskText(
              top.competitor,
              demoMode
            )}.`,
    };
  }, [filteredData, demoMode, isLeaderAccount]);

  const derivedEntityBuilderRecommendations = useMemo<EntityBuilderRecommendation[]>(() => {
    const top = filteredData[0];
    const topCompetitor = top ? maskText(top.competitor, demoMode) : "the leading competitor";
    const topFocus = top
      ? getFocusLabel(top.prompt_focus, top.prompt_focus_label)
      : "the highest-pressure focus area";

    return [
      {
        key: "derived-primary-entity",
        title: "Clarify the primary entity",
        description: `Confirm AI systems connect ${displayCustomerBrand} to the right name, acronym, domain, category, products, programs, and proof points.`,
        action: "Review aliases, entity profile, schema, and core brand description.",
        activationLabel: "Generate Entity Brief",
        activationType: "generate_entity_brief",
        priority: "High",
        entityArea: "entity_clarity",
        recommendationType: "entity_brief",
        source: "derived",
      },
      {
        key: "derived-competitive-relationship",
        title: "Map the competitive relationship",
        description: `${topCompetitor} is creating the strongest visible pressure right now. The next step is to define how ${displayCustomerBrand} should be compared, positioned, and recommended in ${topFocus}.`,
        action: "Build comparison, alternative, and proof language around this relationship.",
        activationLabel: "Generate Comparison Brief",
        activationType: "generate_comparison_brief",
        priority: "Medium",
        entityName: topCompetitor,
        entityArea: "competitive_relationship",
        recommendationType: "comparison_brief",
        source: "derived",
      },
      {
        key: "derived-recommendation-proof",
        title: "Strengthen recommendation proof",
        description: `Add evidence that helps AI systems trust when to recommend ${displayCustomerBrand}: outcomes, credentials, FAQs, testimonials, case examples, citations, and structured data.`,
        action: "Use Agent Smith to turn the top opportunity into a proof-backed action brief.",
        activationLabel: "Generate Proof Checklist",
        activationType: "generate_proof_checklist",
        priority: "Medium",
        entityArea: "recommendation_proof",
        recommendationType: "proof_checklist",
        source: "derived",
      },
    ];
  }, [filteredData, demoMode, displayCustomerBrand]);

  const entityBuilderRecommendations = backendEntityRecommendations.length
    ? backendEntityRecommendations
    : derivedEntityBuilderRecommendations;

  const activeItemCompletedBrief = useMemo(() => {
    if (!activeItem) return null;
    const candidateIdentity = getCompetitiveSemanticIdentity({
      productArea: "competitive",
      entityName: activeItem.competitor,
      title: `Close gap vs ${activeItem.competitor}`,
      opportunity: {
        title: `Close gap vs ${activeItem.competitor}`,
        category: activeItem.prompt_focus_label || activeItem.prompt_focus,
        intent: activeItem.prompt_focus,
        competitors: [activeItem.competitor],
      },
    });
    if (!candidateIdentity.semanticKey) return null;

    return competitiveCompletionMemory.find((brief) => {
      const sourceContext = (brief.source_context || {}) as Record<string, unknown>;
      const identity = getCompetitiveSemanticIdentity({
        productArea: brief.product_area,
        actionType: brief.action_type,
        title: brief.title,
        sourceContext,
        opportunity: sourceContext.opportunity as any,
        accountContext: sourceContext.accountContext as any,
      });
      return identity.semanticKey === candidateIdentity.semanticKey;
    }) || null;
  }, [activeItem, competitiveCompletionMemory]);

  const activeEntityBuilderRecommendations = useMemo(
    () => entityBuilderRecommendations.filter((recommendation) => {
      const identity = getCompetitiveSemanticIdentity({
        productArea: "competitive",
        actionType: recommendation.activationType,
        recommendationType: recommendation.recommendationType,
        title: recommendation.title,
        entityName: recommendation.entityName,
        opportunity: { title: recommendation.title, recommendation: recommendation.action },
      });
      return !identity.semanticKey || !completedCompetitiveSemanticKeys.has(identity.semanticKey);
    }),
    [entityBuilderRecommendations, completedCompetitiveSemanticKeys]
  );

  const visibleEntityBuilderRecommendations = useMemo(
    () => statusFilter === "complete"
      ? []
      : activeEntityBuilderRecommendations.filter((recommendation) => actionTypeFilter === "all" || getCompetitiveActionClassification(recommendation).key === actionTypeFilter),
    [activeEntityBuilderRecommendations, actionTypeFilter, statusFilter]
  );

  const competitiveActionCounts = useMemo(
    () => activeEntityBuilderRecommendations.reduce<Record<string, number>>((counts, recommendation) => {
      const key = getCompetitiveActionClassification(recommendation).key;
      if (key) counts[key] = (counts[key] || 0) + 1;
      return counts;
    }, {}),
    [activeEntityBuilderRecommendations]
  );

  useEffect(() => {
    if (actionTypeFilter !== "all" && visibleEntityBuilderRecommendations.length === 0) {
      setActionTypeFilter("all");
    }
  }, [actionTypeFilter, visibleEntityBuilderRecommendations.length]);

  const focusedEntityBuilderRecommendations = useMemo(
    () => isFocused
      ? sortFocusedCompetitiveRecommendations(visibleEntityBuilderRecommendations)
      : visibleEntityBuilderRecommendations,
    [isFocused, visibleEntityBuilderRecommendations]
  );

  const renderedEntityBuilderRecommendations = isFocused && !showAllRecommendations
    ? focusedEntityBuilderRecommendations.slice(0, focusedVisibleCount)
    : focusedEntityBuilderRecommendations;
  const competitiveInactiveOrCompletedCount = Math.max(0, activeEntityBuilderRecommendations.length - renderedEntityBuilderRecommendations.length) + completedCompetitiveBriefs.length;
  const focusedEntityRecommendation = focusedEntityBuilderRecommendations.find(item => item.key === selectedFocusedRecommendationKey) || focusedEntityBuilderRecommendations[0];
  const focusedCompetitiveOutput = focusedEntityRecommendation
    ? getCompetitivePrescribedOutput(focusedEntityRecommendation, account, runId)
    : { outputType: "Manual review", targetAsset: "Target asset unavailable", audience: "The audience most likely to rely on this decision.", evidencePattern: "A reviewed visibility or recommendation gap is driving this prescription.", expectedResult: "A clearer reviewed asset ready for the next human review.", draftStatus: "Not ready" };

  const visibleCompletedCompetitiveBriefs = useMemo(
    () => statusFilter === "open" ? [] : completedCompetitiveBriefs.filter((brief) => {
      if (actionTypeFilter === "all") return true;
      return getCompetitiveActionClassification({
            key: brief.id,
            title: brief.title || "Completed Competitive Action Brief",
            description: String(brief.why_this_matters || ""),
            action: String(brief.recommended_action || ""),
            activationLabel: formatActionType(brief.action_type),
            activationType: brief.action_type,
            source: "backend",
          }).key === actionTypeFilter;
    }),
    [completedCompetitiveBriefs, actionTypeFilter, statusFilter]
  );

  function handleCompetitiveCompletionChange(updatedBrief: ActionBrief) {
    if (updatedBrief.marked_complete_at) {
      setCompletedCompetitiveBriefs((current) => [updatedBrief, ...current.filter((brief) => brief.id !== updatedBrief.id)]);
      setCompetitiveCompletionMemory((current) => [updatedBrief, ...current.filter((brief) => brief.id !== updatedBrief.id)]);
      return;
    }

    setCompletedCompetitiveBriefs((current) => current.filter((brief) => brief.id !== updatedBrief.id));
    setCompetitiveCompletionMemory((current) => current.filter((brief) => brief.id !== updatedBrief.id));
  }

  const entityBuilderSourceLabel = backendEntityRecommendations.length
    ? "Agent Smith recommendation queue"
    : "Derived starter recommendations";

  if (!account) {
    return (
      <ModuleContainer
        title="AI Opportunity Engine"
        state="unavailable"
        message="This module is not available for this account yet."
      />
    );
  }

  if (status !== "ready") {
    return (
      <ModuleContainer
        title="AI Opportunity Engine"
        state={status}
        message={statusMessage}
      />
    );
  }

  return (
    <ModuleContainer title="AI Opportunity Engine" plain={isFocused}>
      {isFocused && <OpportunityWorkspaceHeader title="Competitive" description="Choose the next competitive priority, review its rationale, then use the existing action tools." />}
      <div className={isFocused ? "oe-action-layout" : undefined}>
      {isFocused && <nav className="oe-navigator" aria-label="Competitive opportunities"><strong>Opportunities</strong>{renderedEntityBuilderRecommendations.map(item => <button type="button" className="oe-opportunity" key={item.key} aria-current={focusedEntityRecommendation?.key === item.key ? "true" : undefined} onClick={() => setSelectedFocusedRecommendationKey(item.key)} title={item.title}><strong>{item.title}</strong><small>{item.entityName || item.recommendationType}</small></button>)}</nav>}
      <div style={{ minWidth: 0 }}>
      <p
        style={{
          marginTop: 12,
          maxWidth: 760,
          fontSize: 16,
          lineHeight: 1.45,
          color: "#475569",
        }}
      >
        {isFocused
          ? "Turn the strongest visibility signal into focused work."
          : "Use this as the AI presence builder: prioritize where competitors are included, where your brand is missing, and what foundation needs to be strengthened so AI systems can recognize, trust, and recommend you more consistently."}
      </p>

      {isFocused && focusedEntityRecommendation && (
        <FocusedWorkSummary
          title={focusedEntityRecommendation.title}
          action={focusedEntityRecommendation.action}
          outputType={focusedCompetitiveOutput.outputType}
          targetAsset={focusedCompetitiveOutput.targetAsset}
          audience={focusedCompetitiveOutput.audience}
          evidencePattern={focusedCompetitiveOutput.evidencePattern}
          expectedResult={focusedCompetitiveOutput.expectedResult}
          draftStatus={focusedCompetitiveOutput.draftStatus}
          accountId={account}
          runId={runId}
          recommendationId={focusedEntityRecommendation.key}
          recommendationKey={focusedEntityRecommendation.key}
          recommendationType={focusedEntityRecommendation.recommendationType}
          targetEntity={focusedEntityRecommendation.entityName}
          recommendedOutput={focusedCompetitiveOutput.outputType}
          liveCaiEnabled={process.env.NODE_ENV !== "test"}
          actionBriefId={focusedEntityRecommendation.actionBriefId}
          accountType={accountType || (mode === "demo" ? "competitive" : mode)}
          entitlementOverride={entitlementOverride}
          accountName={displayCustomerBrand}
        />
      )}

      <div
        style={{
          marginTop: isFocused ? 8 : 18,
          border: "1px solid #bbf7d0",
          borderRadius: 22,
          padding: isFocused ? 12 : 18,
          background: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)",
          ...(isFocused ? { display: "none" } : {}),
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "flex-start",
              marginBottom: isFocused ? 9 : 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#166534",
              }}
            >
              Completed opportunity validation
            </div>
            <div
              style={{
                marginTop: 4,
                maxWidth: 720,
                color: "#475569",
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              Dashboard-level proof that optimization opportunities are being worked, not just identified.
            </div>
          </div>

          {completionStatus === "error" && (
            <div style={{ color: "#92400e", fontSize: 12, fontWeight: 800 }}>
              Metrics unavailable
            </div>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: isFocused ? 8 : 10,
          }}
        >
          {[
            ["This run", completionSummary.currentRun],
            ["Last 30 days", completionSummary.last30],
            [`${completionSummary.currentYear} YTD`, completionSummary.ytd],
            ["All time", completionSummary.allTime],
          ].map(([label, value]) => (
            <div
              key={String(label)}
              style={{
                border: "1px solid #bbf7d0",
                borderRadius: 16,
                background: "#ffffff",
                padding: "12px 14px",
              }}
            >
              <div
                style={{
                  color: "#166534",
                  fontSize: 24,
                  fontWeight: 850,
                  lineHeight: 1,
                }}
              >
                {completionStatus === "loading" ? "—" : value}
              </div>
              <div
                style={{
                  marginTop: 5,
                  color: "#64748b",
                  fontSize: 12,
                  fontWeight: 750,
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: isFocused ? 9 : 12,
            borderTop: "1px solid #bbf7d0",
            paddingTop: 12,
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {completionSummary.byProductArea.map((area) => (
            <div
              key={area.key}
              style={{
                border: "1px solid #bbf7d0",
                borderRadius: 999,
                background: "#ffffff",
                padding: "7px 11px",
                color: "#166534",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {area.label}: {completionStatus === "loading" ? "—" : area.count}
            </div>
          ))}
        </div>

      </div>

      <div
        style={{
          marginTop: isFocused ? 8 : 18,
          border: "1px solid #e2e8f0",
          borderRadius: 22,
          padding: isFocused ? 11 : 18,
          background: "#ffffff",
          ...(isFocused ? { display: "flex", flexDirection: "column" } : {}),
        }}
      >
        <details open={!isFocused} className="oe-supporting-tools" style={isFocused ? { order: 3 } : undefined}>
          <summary>AI Presence Foundation · supporting intelligence</summary>
          <div>
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 750,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#64748b",
                marginBottom: 8,
              }}
            >
              AI Presence Foundation
            </div>
            <div
              style={{
                fontSize: 20,
                lineHeight: 1.25,
                fontWeight: 800,
                color: "#0f172a",
                letterSpacing: "-0.02em",
              }}
            >
              Close visibility gaps while strengthening the entity foundation AI systems use to understand and recommend the brand.
            </div>
          </div>

          <div
            style={{
                  marginTop: isFocused ? 10 : 16,
              borderTop: "1px solid #e2e8f0",
              paddingTop: 14,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 750,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#64748b",
                marginBottom: 10,
              }}
            >
              Run snapshot
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: isFocused ? 8 : 10,
              }}
            >
              {[
                ["Gap signals", foundationMetrics.totalGapSignals],
                ["Competitors", foundationMetrics.uniqueCompetitors],
                ["Focus areas", foundationMetrics.focusAreaCount],
                ["High impact", foundationMetrics.highImpactCount],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 16,
                    background: "#ffffff",
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      color: "#0f172a",
                      fontSize: 22,
                      fontWeight: 800,
                      lineHeight: 1,
                    }}
                  >
                    {value}
                  </div>
                  <div
                    style={{
                      marginTop: 5,
                      color: "#64748b",
                      fontSize: 12,
                      fontWeight: 650,
                      display: "inline-flex",
                      gap: 6,
                      alignItems: "center",
                    }}
                  >
                    {label}
                    <Tooltip text={FOUNDATION_METRIC_TOOLTIPS[String(label)] || "This metric helps explain the current AI presence-building priority."} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: isFocused ? 10 : 18,
            borderTop: "1px solid #e2e8f0",
            paddingTop: 14,
            ...(isFocused ? { order: 3 } : {}),
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 750,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#64748b",
              marginBottom: isFocused ? 8 : 10,
            }}
          >
            Foundation areas strengthened
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
              gap: 10,
            }}
          >
            {FOUNDATION_PILLARS.map((pillar) => (
              <div
                key={pillar.title}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 16,
                  background: "#ffffff",
                  padding: 14,
                }}
              >
                <div
                  style={{
                    color: "#0f172a",
                    fontSize: 14,
                    fontWeight: 800,
                    marginBottom: 6,
                  }}
                >
                  {pillar.title}
                </div>
                <div
                  style={{
                    color: "#64748b",
                    fontSize: 12,
                    lineHeight: 1.45,
                  }}
                >
                  {pillar.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        </details>
        {isFocused && (
          <div data-testid="oe-focused-completed-validation" style={{ order: 4, marginTop: 10, paddingTop: 9, borderTop: "1px solid #bbf7d0", color: "#64748b", fontSize: 11, fontWeight: 800 }}>
            Completed opportunity validation · This run: {completionSummary.currentRun} · Last 30 days: {completionSummary.last30} · All time: {completionSummary.allTime}
          </div>
        )}

        <div
          style={{
            marginTop: 18,
            borderTop: "1px solid #e2e8f0",
            paddingTop: 14,
            ...(isFocused ? { order: 1, marginTop: 0, borderTop: 0, paddingTop: 0, display: "contents" } : {}),
          }}
        >
          <details open={!isFocused} className="oe-supporting-tools" style={isFocused ? { order: 3 } : undefined}><summary>Recommendation filters and controls</summary><div
              style={{
                display: "flex",
                flexWrap: "wrap",
              justifyContent: "space-between",
                gap: 10,
                alignItems: "flex-end",
                marginBottom: 10,
                ...(isFocused ? { order: 1, marginBottom: 8 } : {}),
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 750,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#64748b",
                }}
              >
                Entity Builder recommendations
              </div>
              <div
                style={{
                  marginTop: 4,
                  maxWidth: 720,
                  color: "#64748b",
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                {isFocused ? "Choose a next step from the active recommendation list." : `${entityBuilderSourceLabel}. These may become checklists, schema updates, alias reviews, suppression reviews, comparison briefs, or proof plans — not just new articles.`}
              </div>
            </div>
            <div
              style={{
                border: "1px solid #bae6fd",
                borderRadius: 999,
                padding: "6px 10px",
                background: backendEntityRecommendations.length ? "#e0f2fe" : "#f8fafc",
                color: backendEntityRecommendations.length ? "#0369a1" : "#64748b",
                fontSize: 11,
                fontWeight: 800,
                whiteSpace: "nowrap",
              }}
            >
              {isFocused ? `${activeEntityBuilderRecommendations.length} active recommendations${competitiveInactiveOrCompletedCount > 0 ? ` · ${competitiveInactiveOrCompletedCount} completed or inactive hidden` : ""}` : backendEntityRecommendations.length ? "Reviewed recommendations" : "Starter recommendations"}
            </div>
          </div>

          <div style={{ marginBottom: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", ...(isFocused ? { order: 1, marginBottom: 8 } : {}) }}>
            <span style={{ color: "#64748b", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>Filter Competitive briefs</span>
            <span style={{ color: "#64748b", fontSize: 11, fontWeight: 800 }}>Status:</span>
            {[{ value: "all", label: "All", count: activeEntityBuilderRecommendations.length + completedCompetitiveBriefs.length }, { value: "open", label: "Open", count: activeEntityBuilderRecommendations.length }, { value: "complete", label: "Complete", count: completedCompetitiveBriefs.length }].map((filter) => {
              const active = statusFilter === filter.value;
              return <button key={filter.value} type="button" title={filter.value === "all" ? "Show active and completed Competitive briefs in their separate sections." : `${filter.label} completion status.`} aria-pressed={active} disabled={filter.count === 0 && !active} onClick={() => setStatusFilter(filter.value)} className="toren-control toren-control--secondary">{filter.label} ({filter.count})</button>;
            })}
          </div>

          <div style={isFocused ? { order: 1 } : undefined}><ActionTypeColorKey activeFilter={actionTypeFilter} onFilter={setActionTypeFilter} counts={competitiveActionCounts} /></div></details>

            <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: isFocused ? 9 : 12,
              ...(isFocused ? { order: 2 } : {}),
            }}
          >
            {visibleEntityBuilderRecommendations.length === 0 ? (
              <div style={{ color: "#64748b", fontSize: 12 }}>
                <p style={{ margin: 0 }}>No active Competitive briefs match the selected filter.</p>
                {actionTypeFilter !== "all" && <button type="button" onClick={() => setActionTypeFilter("all")} style={{ marginTop: 7, border: 0, background: "transparent", color: "#0e7490", fontSize: 12, fontWeight: 800, cursor: "pointer", padding: 0 }}>Clear filter</button>}
              </div>
            ) : renderedEntityBuilderRecommendations.map((recommendation) => {
              const isSecondaryFocused = isFocused && recommendation.key !== focusedEntityRecommendation?.key;
              return <div
                key={recommendation.key}
                style={{
                  ...(isFocused ? { border: "0", padding: 0, background: "transparent" } : getEntityBuilderRecommendationStyle(recommendation)),
                  display: isFocused && isSecondaryFocused ? "none" : undefined,
                  borderRadius: isFocused ? 0 : 16,
                  padding: isFocused ? 0 : 14,
                }}
              >
                {isSecondaryFocused ? (
                  <FocusedSecondaryCompetitiveRow
                    recommendation={recommendation}
                    selected={selectedFocusedRecommendationKey === recommendation.key}
                    onSelect={() => setSelectedFocusedRecommendationKey((current) => current === recommendation.key ? null : recommendation.key)}
                    account={account}
                    runId={runId}
                    displayCustomerBrand={displayCustomerBrand}
                    onCompletionChange={handleCompetitiveCompletionChange}
                  />
                ) : <>
                {!isFocused && <><div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      color: "#0f172a",
                      fontSize: 14,
                      fontWeight: 800,
                    }}
                  >
                    {recommendation.title}
                  </div>
                  {recommendation.priority && (
                    <span
                      style={{
                        borderRadius: 999,
                        padding: "3px 7px",
                        background: "#ffffff",
                        color: "#0369a1",
                        fontSize: 10,
                        fontWeight: 800,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {recommendation.priority}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    color: "#475569",
                    fontSize: 12,
                    lineHeight: 1.45,
                  }}
                >
                  {recommendation.description}
                </div>
                <div
                  style={{
                    marginTop: 8,
                    color: "#0369a1",
                    fontSize: 12,
                    fontWeight: 800,
                    lineHeight: 1.35,
                  }}
                >
                  {recommendation.action}
                </div>
                <div
                  style={{
                    marginTop: 10,
                    borderTop: "1px solid #bae6fd",
                    paddingTop: 8,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 8,
                    alignItems: "center",
                    color: "#0f172a",
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  <span>Recommended activation</span>
                  <span style={{ color: "#0369a1" }}>{recommendation.activationLabel}</span>
                </div></>}

                <OpportunityActionBriefPanel
                  accountId={account}
                  runId={runId ?? null}
                  productArea="competitive"
                  opportunity={{
                    title: recommendation.title,
                    summary: recommendation.description,
                    recommendation: recommendation.action,
                    category:
                      recommendation.entityArea ||
                      recommendation.recommendationType ||
                      "entity_builder",
                    intent:
                      recommendation.activationType ||
                      recommendation.activationLabel ||
                      "generate_action_brief",
                    priority: recommendation.priority,
                    evidence: [
                      ...(isFocused ? [] : [`Source: ${recommendation.source}`]),
                      `Recommended activation: ${recommendation.activationLabel}`,
                      recommendation.entityName
                        ? `Entity: ${recommendation.entityName}`
                        : "",
                    ].filter(Boolean),
                    competitors: recommendation.entityName
                      ? [recommendation.entityName]
                      : undefined,
                  }}
                  accountContext={{
                    accountName: displayCustomerBrand,
                    category:
                      recommendation.entityArea ||
                      recommendation.recommendationType ||
                      "AI presence builder",
                    competitors: recommendation.entityName
                      ? [recommendation.entityName]
                      : undefined,
                  }}
                  onCompletionChange={handleCompetitiveCompletionChange}
                />
                </>}
              </div>;
            })}
          </div>

          {isFocused && (focusedEntityBuilderRecommendations.length > focusedVisibleCount || filteredData.length > 0) && (
            <button
              type="button"
              data-testid="oe-view-all-opportunities"
              aria-expanded={showAllRecommendations}
              onClick={() => setShowAllRecommendations((current) => !current)}
              className="toren-control toren-control--tertiary"
              style={{ marginTop: 12, ...(isFocused ? { order: 2 } : {}) }}
            >
              {showAllRecommendations ? "Show fewer" : "View all opportunities"}
            </button>
          )}

          {recentCompletedWork.length > 0 && (
            <details data-testid="oe-recent-completed-work" style={{ marginTop: 14, ...(isFocused ? { order: 5 } : {}) }}>
              <summary style={{ cursor: "pointer", color: "#64748b", fontSize: 12, fontWeight: 800 }}>Recently completed work ({recentCompletedWork.length})</summary>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 9, marginTop: 9 }}>
                {recentCompletedWork.map((item) => (
                  <div key={item.id} style={{ border: "1px solid #bbf7d0", borderRadius: 14, background: "#f0fdf4", padding: "10px 12px" }}>
                    <div style={{ color: "#0f172a", fontSize: 12, fontWeight: 850, lineHeight: 1.35 }}>{item.title || "Completed opportunity"}</div>
                    <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6, color: "#64748b", fontSize: 11, fontWeight: 750, lineHeight: 1.3 }}>
                      <span>{formatCompletionDate(item.marked_complete_at)}</span><span>•</span><span>{formatProductArea(item.product_area)}</span><span>•</span><span>{formatActionType(item.action_type)}</span>
                      {item.completed_by && <><span>•</span><span>{item.completed_by}</span></>}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {visibleCompletedCompetitiveBriefs.length > 0 && (
            <details style={{ marginTop: 18, ...(isFocused ? { order: 6 } : {}) }}>
              <summary style={{ cursor: "pointer", color: "#166534", fontSize: 14, fontWeight: 900, borderTop: "1px solid #bbf7d0", paddingTop: 14 }}>
                Completed Competitive Action Briefs ({visibleCompletedCompetitiveBriefs.length})
              </summary>
              <p style={{ color: "#64748b", fontSize: 12, margin: "8px 0 12px" }}>Completed items are removed from the active Competitive queue. Use Mark as incomplete to restore them.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {visibleCompletedCompetitiveBriefs.map((brief) => {
                  const sourceContext = (brief.source_context || {}) as Record<string, any>;
                  const sourceOpportunity = sourceContext.opportunity || {};
                  const title = sourceOpportunity.title || brief.title || "Completed Competitive Action Brief";
                  return (
                    <div key={brief.id} style={{ border: "1px solid #bbf7d0", borderRadius: 16, background: "#f0fdf4", padding: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <strong style={{ color: "#0f172a", fontSize: 14 }}>{title}</strong>
                        <span style={{ border: "1px solid #bbf7d0", borderRadius: 999, padding: "4px 8px", background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 900 }}>Completed</span>
                      </div>
                      <OpportunityActionBriefPanel
                        accountId={account}
                        runId={runId ?? null}
                        productArea="competitive"
                        opportunity={{
                          title,
                          summary: sourceOpportunity.summary || brief.why_this_matters || "Completed Competitive action brief.",
                          recommendation: sourceOpportunity.recommendation || brief.recommended_action || undefined,
                          category: sourceOpportunity.category || "competitive",
                          intent: sourceOpportunity.intent || brief.action_type || "competitive_action",
                          priority: sourceOpportunity.priority,
                          evidence: sourceOpportunity.evidence || [],
                          competitors: sourceOpportunity.competitors,
                        }}
                        accountContext={{ accountName: displayCustomerBrand, category: sourceOpportunity.category || "Competitive" }}
                        initialActionBrief={brief as ActionBrief}
                        onCompletionChange={handleCompetitiveCompletionChange}
                      />
                    </div>
                  );
                })}
              </div>
            </details>
          )}
        </div>
      </div>

      {(!isFocused || showAllRecommendations) && <>
      <div
        style={{
          marginTop: 18,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
        }}
      >
        <label
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            fontSize: 12,
            fontWeight: 700,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          Focus Area
          <select
            value={selectedPromptFocus}
            onChange={(event) => {
              setSelectedPromptFocus(event.target.value);
              setActiveIndex(0);
            }}
            style={{
              minWidth: 220,
              border: "1px solid #cbd5e1",
              borderRadius: 12,
              padding: "10px 12px",
              fontSize: 14,
              fontWeight: 600,
              color: "#0f172a",
              background: "#ffffff",
            }}
          >
            {promptFocusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div
          style={{
            fontSize: 13,
            color: "#64748b",
            alignSelf: "end",
            paddingBottom: 10,
          }}
        >
          {`Showing ${filteredData.length} of ${actionableData.length} actionable opportunities`}
        </div>
      </div>

      {filteredData.length === 0 && (
        <div
          style={{
            marginTop: 18,
            border: "1px solid #e2e8f0",
            borderRadius: 18,
            padding: 18,
            color: "#64748b",
            background: "#f8fafc",
          }}
        >
          No opportunities match this focus area yet.
        </div>
      )}

      <>
        {summary && (
          <div
            style={{
              marginTop: 22,
              marginBottom: 22,
              border: "1px solid #e2e8f0",
              borderRadius: 24,
              background: "#f8fafc",
              padding: 20,
              boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#64748b",
              }}
            >
              Opportunity summary
            </div>

            <p
              style={{
                marginTop: 12,
                marginBottom: 0,
                fontSize: 24,
                lineHeight: 1.18,
                fontWeight: 750,
                letterSpacing: "-0.025em",
                color: "#020617",
              }}
            >
              {summary.narrative}
            </p>

            <div
              style={{
                marginTop: 16,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                fontSize: 14,
                color: "#475569",
                lineHeight: 1.4,
              }}
            >
              <span>{summary.total} ranked opportunities</span>

              {summary.highImpactCount > 0 && (
                <>
                  <span style={{ color: "#cbd5e1" }}>•</span>
                  <span>{summary.highImpactCount} high impact</span>
                </>
              )}

              <span style={{ color: "#cbd5e1" }}>•</span>

              <span>
                Focus area: {" "}
                {CATEGORY_LABELS[summary.topCategory] ||
                  CATEGORY_LABELS.Opportunity}
              </span>
            </div>
          </div>
        )}

        {activeItem && (
          <div style={{ marginTop: 22 }}>
            <OpportunityCard
              item={{
                ...activeItem,
                total_gap_count: activeItem.gap_count,
                primary_category: activeItem.prompt_focus,
                category_label: activeItem.prompt_focus_label,
              }}
              demoMode={demoMode}
              customerBrand={displayCustomerBrand}
              isLeaderAccount={isLeaderAccount}
              rank={activeIndex + 1}
              total={filteredData.length}
              onPrev={() => setActiveIndex((prev) => Math.max(prev - 1, 0))}
              onNext={() =>
                setActiveIndex((prev) =>
                  Math.min(prev + 1, filteredData.length - 1)
                )
              }
              canPrev={activeIndex > 0}
              canNext={activeIndex < filteredData.length - 1}
              accountId={account}
              runId={activeItem.run_id || null}
              accountCategory={activeItem.prompt_focus_label || activeItem.prompt_focus}
              completedActionBrief={activeItemCompletedBrief as ActionBrief | null}
              onCompletionChange={handleCompetitiveCompletionChange}
            />
          </div>
        )}

        {peekItems.length > 1 && (
          <div
            style={{
              marginTop: 22,
              paddingTop: 2,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#94a3b8",
                marginBottom: 10,
              }}
            >
              More opportunities
            </div>

            <div
        style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {peekItems.map((item, idx) => {
                const focusLabel = getFocusLabel(item.prompt_focus, item.prompt_focus_label);

                return (
                  <button
                    key={`${item.competitor}-${item.prompt_focus}-${idx}`}
                    type="button"
                    onClick={() => setActiveIndex(idx)}
                    style={{
                      border:
                        idx === activeIndex
                          ? "1px solid #0f172a"
                          : "1px solid #e2e8f0",
                      background: idx === activeIndex ? "#0f172a" : "#f8fafc",
                      color: idx === activeIndex ? "#ffffff" : "#475569",
                      borderRadius: 999,
                      padding: "7px 11px",
                      fontSize: 12,
                      fontWeight: 650,
                      cursor: "pointer",
                      boxShadow:
                        idx === activeIndex
                          ? "0 6px 16px rgba(15, 23, 42, 0.14)"
                          : "none",
                    }}
                  >
                    {maskText(item.competitor, demoMode)} · {focusLabel} ({item.gap_count})
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </>
      </>}
      </div></div>
    </ModuleContainer>
  );
}
